/* Klassy QuickView drawer — custom element <klassy-quickview-modal>
 *
 * Reemplaza Dawn `<quick-add-modal>` + `quick-add.js` con un mecanismo más
 * ligero y rápido inspirado en doreandrose.com:
 *   - Fetch a `?section_id=quickadd-product-info` (5-10 KB) en lugar de la URL
 *     completa del PDP (~113 KB).
 *   - Animación de panel + underlay paralela al fetch (no espera contenido).
 *   - Cero re-orden DOM post-render (la sección viene en el orden correcto).
 *   - Underlay separado con opacity 0→1 (sin backdrop-filter blur GPU-pesado).
 *
 * Markup esperado (lo renderiza klassy-upsell-block.liquid):
 *   <klassy-quickview-modal id="X" data-klassy-drawer>
 *     <div class="klassy-drawer-underlay" aria-hidden="true"></div>
 *     <div class="klassy-drawer-panel" role="dialog" aria-modal="true">
 *       <button class="klassy-drawer-close" data-quickview-close>×</button>
 *       <div class="klassy-drawer-content"></div>
 *     </div>
 *   </klassy-quickview-modal>
 *
 * Trigger esperado:
 *   <button data-klassy-quickview-trigger
 *           aria-controls="X"
 *           data-product-url="/products/handle"
 *           data-section-id="quickadd-product-info">QUICK VIEW</button>
 */

(function () {
  if (window.__klassyQuickviewLoaded) return;
  window.__klassyQuickviewLoaded = true;

  const TRANSITION_MS = 300;
  const SECTION_ID_DEFAULT = 'quickadd-product-info';

  class KlassyQuickviewModal extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.isLoading = false;
      this.lastFocus = null;
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onUnderlayClick = this._onUnderlayClick.bind(this);
    }

    connectedCallback() {
      // Portal pattern: move the modal to document.body to escape any ancestor
      // that creates a new containing block for `position: fixed` (transforms,
      // will-change, filter, perspective on parents break fixed positioning).
      // Without this, the modal renders inside the upsell flex flow, gets
      // constrained to ~500px width and ~100px Y offset, hiding the underlay.
      if (this.parentElement && this.parentElement !== document.body && !this.dataset.portaled) {
        this.dataset.portaled = 'true';
        document.body.appendChild(this);
        return; // connectedCallback will re-fire after re-attachment
      }

      this.underlayEl = this.querySelector('.klassy-drawer-underlay');
      this.panelEl = this.querySelector('.klassy-drawer-panel');
      this.contentEl = this.querySelector('.klassy-drawer-content');

      this.querySelectorAll('[data-quickview-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      if (this.underlayEl) {
        this.underlayEl.addEventListener('click', this._onUnderlayClick);
      }
    }

    async open(productUrl, sectionId) {
      if (this.isOpen || this.isLoading) return;
      this.isLoading = true;
      this.lastFocus = document.activeElement;

      // Save scroll Y + scroll PDP gallery into view ANTES de overflow:hidden,
      // para que el underlay frosted-glass tenga la PDP detrás (efecto Dore).
      // Sin esto, el usuario que abre QV desde el upsell ve el body blanco.
      this._savedScrollY = window.scrollY;
      const gallery = document.querySelector('.product__media-wrapper, .klassy-pdp__media, media-gallery');
      if (gallery) {
        const galleryY = gallery.getBoundingClientRect().top + window.scrollY;
        // Scroll instantáneo (smooth no se ve mientras se activa overflow:hidden)
        window.scrollTo({ top: Math.max(0, galleryY - 80), behavior: 'instant' });
      }

      // Kick off the visual animation immediately — DO NOT wait for fetch.
      this.setAttribute('open', '');
      document.documentElement.classList.add('klassy-quickview-active');
      document.addEventListener('keydown', this._onKeyDown);

      // Build URL with section_id + version cache-buster (bump on Liquid changes
      // to force Shopify page_cache regeneration; page_cache key = full URL).
      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('section_id', sectionId || SECTION_ID_DEFAULT);
        url.searchParams.set('_qv_v', '20260531-1700');
        const startedAt = performance.now();
        const response = await fetch(url.toString(), {
          headers: { 'Accept': 'text/html' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('QV fetch ' + response.status);
        const html = await response.text();
        const fetchMs = Math.round(performance.now() - startedAt);
        this._injectContent(html);
        this.isOpen = true;
        this.isLoading = false;
        // Focus first focusable element inside drawer for accessibility
        const firstFocusable = this.contentEl.querySelector(
          'input:not([type="hidden"]):not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]), select, textarea, a[href]'
        );
        if (firstFocusable) firstFocusable.focus({ preventScroll: true });
        this.dispatchEvent(
          new CustomEvent('klassy-quickview:opened', {
            detail: { url: productUrl, fetchMs },
            bubbles: true,
          })
        );
      } catch (err) {
        console.error('[klassy-quickview] open failed:', err);
        this._showError();
      }
    }

    close() {
      if (!this.isOpen && !this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.isOpen = false;
      this.isLoading = false;
      document.documentElement.classList.remove('klassy-quickview-active');
      document.removeEventListener('keydown', this._onKeyDown);

      // Restaurar scroll a la posición previa al abrir QV.
      if (this._savedScrollY != null) {
        const y = this._savedScrollY;
        this._savedScrollY = null;
        // Pequeño defer para no interferir con la animación slide-out del panel.
        requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' }));
      }

      // Dispatch `modalClosed` INMEDIATAMENTE para que Dawn product-form.js
      // arranque cart-drawer.renderContents() en PARALELO con el slide-out
      // del quickview. Patrón Dore: ambas animaciones overlapean (uno sale
      // por la derecha mientras el otro entra por la derecha) dando una
      // sensación clean de continuidad — no "de golpe". Ambos drawers usan
      // 0.3s cubic-bezier(0.215, 0.61, 0.355, 1) para sincronizar.
      document.body.dispatchEvent(new CustomEvent('modalClosed', { bubbles: true }));

      // Tras la animación de close, limpiamos el contenido para la próxima
      // apertura (evita flash del producto anterior).
      window.setTimeout(() => {
        if (!this.hasAttribute('open') && this.contentEl) {
          this.contentEl.innerHTML = '';
        }
      }, TRANSITION_MS + 50);

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        try { this.lastFocus.focus({ preventScroll: true }); } catch (e) {}
      }
      this.dispatchEvent(new CustomEvent('klassy-quickview:closed', { bubbles: true }));
    }

    // Compatibilidad con Dawn `quick-add-modal.hide(animate)` — product-form.js
    // llama a este método tras un cart-add success cuando detecta que el form
    // está dentro de un modal. Delegamos a close() (la animación slide-out + el
    // dispatch del `modalClosed` event ya están en close()).
    hide(_animate) {
      this.close();
    }

    _injectContent(html) {
      if (!this.contentEl) return;
      this.contentEl.innerHTML = html;
      // Re-execute any inline <script> nodes that arrived in the fragment.
      // (innerHTML insertion does NOT run script tags by default.)
      this.contentEl.querySelectorAll('script').forEach((oldScript) => {
        const newScript = document.createElement('script');
        for (const attr of Array.from(oldScript.attributes)) {
          newScript.setAttribute(attr.name, attr.value);
        }
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }

    _showError() {
      if (this.contentEl) {
        this.contentEl.innerHTML =
          '<div style="padding:32px 24px;text-align:center;font-family:NeueHaasDisplayRoman,sans-serif;color:#1c1b1b">Could not load product. Please try again.</div>';
      }
      this.isLoading = false;
    }

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    }

    _onUnderlayClick(e) {
      e.preventDefault();
      this.close();
    }
  }

  if (!customElements.get('klassy-quickview-modal')) {
    customElements.define('klassy-quickview-modal', KlassyQuickviewModal);
  }

  // Global delegated trigger handler — survives DOM swaps and event re-binding
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-klassy-quickview-trigger]');
    if (!trigger) return;
    e.preventDefault();
    const modalId = trigger.getAttribute('aria-controls') || trigger.dataset.modalId;
    const productUrl = trigger.dataset.productUrl;
    const sectionId = trigger.dataset.sectionId || SECTION_ID_DEFAULT;
    if (!modalId || !productUrl) {
      console.warn('[klassy-quickview] trigger missing aria-controls or data-product-url', trigger);
      return;
    }
    const modal = document.getElementById(modalId);
    if (!modal || typeof modal.open !== 'function') {
      console.warn('[klassy-quickview] modal not found or not a klassy-quickview-modal:', modalId);
      return;
    }
    modal.open(productUrl, sectionId);
  });

  // Gallery prev/next arrow handler (independiente de Dawn slider-component porque
  // usamos clases custom .klassy-qv-arrow / .klassy-qv-slides que Dawn no reconoce).
  // Scroll smooth horizontal de 1 slide-width al click.
  document.addEventListener('click', function (e) {
    const arrow = e.target.closest('.klassy-qv-arrow');
    if (!arrow) return;
    e.preventDefault();
    const slider = arrow.closest('.klassy-qv-slider') || arrow.parentElement;
    if (!slider) return;
    const slides = slider.querySelector('.klassy-qv-slides');
    if (!slides) return;
    const isNext = arrow.classList.contains('klassy-qv-arrow--next');
    const slideWidth = slides.getBoundingClientRect().width;
    if (slideWidth <= 0) return;
    slides.scrollBy({ left: isNext ? slideWidth : -slideWidth, behavior: 'smooth' });
  });

  // ============================================================================
  // Color filter — replica patrón Klassy `color-filter.js` dentro del drawer.
  // Slides con data-color-filter matching la option1 del checked color → visibles.
  // Resto → display:none vía CSS (requiere html[data-color-filter-ready]).
  // ============================================================================
  function getCheckedColor(modal) {
    const radio = modal.querySelector(
      'input[type="radio"]:checked[name*="Color" i], input[type="radio"]:checked[name*="Colour" i]'
    );
    return radio ? (radio.value || '').trim() : null;
  }

  // ============================================================================
  // Preload helpers — para evitar flash blanco al cambiar de color.
  // Cada <img> recibe data-preloaded="true" cuando completa, y el <li> recibe
  // is-img-loaded (clase) para activar fade-in vía CSS.
  // ============================================================================
  function markLoaded(img) {
    if (!img) return;
    img.dataset.preloaded = 'true';
    const slide = img.closest('.klassy-qv-slide');
    if (slide) slide.classList.add('is-img-loaded');
  }

  function preloadImagesForColor(modal, color) {
    if (!modal || !color) return Promise.resolve();
    const slides = modal.querySelectorAll(`.klassy-qv-slide[data-color-filter="${CSS.escape(color)}"]`);
    const promises = [];
    slides.forEach((slide) => {
      const img = slide.querySelector('img.klassy-qv-slide__img');
      if (!img) return;
      if (img.dataset.preloaded === 'true') {
        slide.classList.add('is-img-loaded');
        return;
      }
      if (img.complete && img.naturalHeight > 0) {
        markLoaded(img);
        return;
      }
      // Estrategia: fetchar la URL via Image() separado → garantiza HTTP request
      // independiente de la decisión del browser sobre loading=lazy. La response
      // entra al HTTP cache; cuando el <img> real se hace visible, sirve from
      // cache instantáneo.
      img.loading = 'eager';
      img.fetchPriority = 'high';
      const url = img.currentSrc || img.src;
      if (!url) return;
      const preloader = new Image();
      if (img.srcset) preloader.srcset = img.srcset;
      if (img.sizes) preloader.sizes = img.sizes;
      preloader.src = url;
      promises.push(
        new Promise((resolve) => {
          const onDone = () => { markLoaded(img); resolve(); };
          if (preloader.complete && preloader.naturalHeight > 0) {
            onDone();
            return;
          }
          preloader.addEventListener('load', onDone, { once: true });
          preloader.addEventListener('error', () => resolve(), { once: true });
          // Fallback timeout — no bloquear más de 3s por imagen.
          setTimeout(() => { if (img.dataset.preloaded !== 'true') resolve(); }, 3000);
          // También escuchar load del propio <img> (puede llegar primero).
          img.addEventListener('load', onDone, { once: true });
        })
      );
    });
    return Promise.all(promises);
  }

  // Network-aware: skip preload progresivo en conexiones muy lentas.
  function isSlowConnection() {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    if (c.effectiveType && /^(slow-2g|2g)$/i.test(c.effectiveType)) return true;
    return false;
  }

  // Al abrir el drawer: cargar las del color activo inmediato; el resto en paralelo.
  // El browser limita a ~6 conexiones concurrentes por origen, así que disparar
  // todas las imágenes a la vez sirve naturalmente como cola.
  function preloadProgressive(modal) {
    if (!modal) return;
    const current = getCheckedColor(modal);
    const colors = Array.from(
      new Set(
        Array.from(modal.querySelectorAll('.klassy-qv-slide[data-color-filter]'))
          .map((s) => s.dataset.colorFilter)
          .filter(Boolean)
      )
    );
    // 1) Inmediato: color actual (prioridad alta).
    if (current) preloadImagesForColor(modal, current);
    if (isSlowConnection()) return;
    // 2) Resto: en paralelo via setTimeout(0) — el browser encola y procesa.
    const others = colors.filter((c) => c !== current);
    setTimeout(() => {
      others.forEach((color) => preloadImagesForColor(modal, color));
    }, 80);
  }

  // Bind onload de cada img para marcar is-img-loaded incluso sin preload manual.
  function bindImageLoadListeners(modal) {
    modal.querySelectorAll('.klassy-qv-slide img.klassy-qv-slide__img').forEach((img) => {
      if (img.__klassyQvBound) return;
      img.__klassyQvBound = true;
      if (img.complete && img.naturalHeight > 0) {
        markLoaded(img);
      } else {
        img.addEventListener('load', () => markLoaded(img), { once: true });
      }
    });
  }

  // Hover/focus en swatch radio → precarga ese color anticipadamente.
  function bindSwatchPrefetch(modal) {
    if (modal.__klassyQvSwatchBound) return;
    modal.__klassyQvSwatchBound = true;
    const onPrefetch = (e) => {
      const t = e.target;
      const label = t && t.closest && t.closest('label[for]');
      const input = label
        ? modal.querySelector('#' + CSS.escape(label.getAttribute('for')))
        : (t && t.matches && t.matches('input[type=radio]') ? t : null);
      if (!input) return;
      if (!/colou?r/i.test(input.name || '')) return;
      const color = (input.value || '').trim();
      if (color) preloadImagesForColor(modal, color);
    };
    modal.addEventListener('pointerenter', onPrefetch, { capture: true });
    modal.addEventListener('focusin', onPrefetch, { capture: true });
  }

  function applyColorFilter(modal) {
    if (!modal) return;
    const color = getCheckedColor(modal);
    if (!color) return;
    // Preload del nuevo color ANTES de aplicar el toggle. La promesa la dejamos
    // pendiente (no await) — el toggle CSS aplica inmediatamente; las imágenes
    // que ya estén en cache aparecen al instante, las que aún no se mostrarán
    // sobre el placeholder gris (CSS) hasta que termine la carga + fade-in.
    preloadImagesForColor(modal, color);
    document.documentElement.setAttribute('data-color-filter-ready', '');
    modal.querySelectorAll('.klassy-qv-slide[data-color-filter]').forEach((slide) => {
      slide.classList.toggle('is-color-active', slide.dataset.colorFilter === color);
    });
    // Orden crítico:
    //   1. scrollTo({left:0, behavior:'auto'}) PRIMERO — resetea scrollLeft
    //      al inicio del color. Si lo hacemos después de update(), update()
    //      calcula currentPage/disabled con scrollLeft del color anterior.
    //   2. initPages() — recomputa sliderItemsToShow filtrando los visibles.
    //   3. update() — sincroniza currentPage y next/prev disabled con scrollLeft=0.
    //   behavior:'auto' es instantáneo (smooth puede ser interrumpido por
    //   scroll-snap-type: x mandatory o por clicks rápidos del user).
    const slides = modal.querySelector('.klassy-qv-slides');
    if (slides) {
      const firstVisible = modal.querySelector('.klassy-qv-slide.is-color-active');
      const offset = firstVisible ? Math.max(0, firstVisible.offsetLeft - slides.offsetLeft) : 0;
      slides.scrollTo({ left: offset, behavior: 'auto' });
    }
    modal.querySelectorAll('slider-component').forEach((s) => {
      if (typeof s.initPages === 'function') {
        try { s.initPages(); } catch (_) {}
      }
      if (typeof s.update === 'function') {
        try { s.update(); } catch (_) {}
      }
      // Override de arrows: NO basta con reasignar s.onButtonClick — Dawn ya
      // registró el listener con .bind(this) en el constructor; esa referencia
      // queda fija. Si cambiamos la propiedad, el listener original sigue
      // corriendo Y el override también → doble scroll (+500 +500 = +1000).
      // Solución: cloneNode del button (no copia event listeners), swap en
      // DOM, registrar nuestro listener en el clon.
      if (!s.__klassyQvButtonsPatched) {
        s.__klassyQvButtonsPatched = true;
        const slider = s.slider;
        const makeHandler = (direction) => function (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const items = s.sliderItemsToShow || [];
          if (items.length < 2 || !slider) return;
          const sL = slider.scrollLeft;
          let curIdx = 0;
          for (let i = 0; i < items.length; i++) {
            if (items[i].offsetLeft <= sL + 5) curIdx = i;
            else break;
          }
          const targetIdx = direction === 'next'
            ? Math.min(items.length - 1, curIdx + 1)
            : Math.max(0, curIdx - 1);
          slider.scrollTo({ left: items[targetIdx].offsetLeft, behavior: 'auto' });
          if (typeof s.update === 'function') {
            try { s.update(); } catch (_) {}
          }
        };
        ['next', 'previous'].forEach((dir) => {
          const oldBtn = s.querySelector('button[name="' + dir + '"]');
          if (!oldBtn) return;
          const newBtn = oldBtn.cloneNode(true);
          oldBtn.parentNode.replaceChild(newBtn, oldBtn);
          newBtn.addEventListener('click', makeHandler(dir));
          // Re-cachear referencia en el slider-component para que update()
          // disable/enable funcione sobre el nuevo botón.
          if (dir === 'next') s.nextButton = newBtn;
          else s.prevButton = newBtn;
        });
      }
    });
  }

  // Resuelve la variante seleccionada (color + talla) desde el mapa
  // data-qv-variants y escribe AL INSTANTE el id en input[name="id"] del form.
  // CRÍTICO: el updateVariantInputs de Dawn busca #product-form-{section} pero el
  // form del QV es #quickadd-product-form-{section}, así que Dawn NUNCA actualizaba
  // el id → el carrito añadía siempre la variante por defecto (bug reportado: eliges
  // naranja y añade rosa). Esto lo arregla de raíz y además mata la race (clic en
  // ATC antes de que termine el fetch de sección). (audit 2026-05-31)
  function syncVariantId(modal) {
    if (!modal) return;
    var dataEl = modal.querySelector('[data-qv-variants]');
    var form = modal.querySelector('product-form form') || modal.querySelector('form[data-type="add-to-cart-form"]');
    if (!dataEl || !form) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!Array.isArray(variants)) return;
    // valores seleccionados en orden de fieldset (= orden option1/option2/option3)
    var fieldsets = modal.querySelectorAll('variant-selects fieldset.product-form__input');
    var selected = Array.prototype.map.call(fieldsets, function (fs) {
      var c = fs.querySelector('input[type="radio"]:checked');
      return c ? c.value : null;
    });
    if (!selected.length) return;
    var match = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i], ok = true;
      for (var j = 0; j < selected.length; j++) {
        if (selected[j] == null) continue;
        if (String(v.o[j] == null ? '' : v.o[j]) !== String(selected[j])) { ok = false; break; }
      }
      if (ok) { match = v; break; }
    }
    var idInput = form.querySelector('input[name="id"]');
    var btn = modal.querySelector('button[name="add"]');
    if (!idInput) return;
    if (match) {
      idInput.value = match.id;
      idInput.removeAttribute('disabled');
      if (btn) {
        if (match.a) { if (!btn.classList.contains('loading')) btn.removeAttribute('disabled'); }
        else { btn.setAttribute('disabled', 'disabled'); }
      }
    } else if (btn) {
      btn.setAttribute('disabled', 'disabled'); // combinación inexistente
    }
  }

  // Cualquier cambio de variante (color o talla) dentro de un drawer abierto →
  // fijar el id AL INSTANTE; el color además re-aplica el filtro de imágenes.
  document.addEventListener('change', function (e) {
    const radio = e.target;
    if (!radio || radio.type !== 'radio') return;
    const modal = radio.closest('klassy-quickview-modal[open]');
    if (!modal) return;
    syncVariantId(modal);
    if (/colou?r/i.test(radio.name || '')) applyColorFilter(modal);
  });

  // Cuando un drawer se acaba de abrir y cargar el contenido:
  //   1) Override product-info.updateMedia → no-op. Sin esto, Dawn re-fetcha
  //      la sección al cambiar variant y reordena/prepend los <li> slides en
  //      el media gallery, causando un flash de re-attachment de las <img>.
  //      Mi color-filter ya maneja la visibilidad de slides, así que el
  //      updateMedia de Dawn es redundante en el drawer.
  //   2) Aplicar color filter por primera vez al color por defecto.
  document.addEventListener('klassy-quickview:opened', function (e) {
    const modal = e.target.closest('klassy-quickview-modal') || e.target;
    if (!modal || !modal.tagName || modal.tagName.toLowerCase() !== 'klassy-quickview-modal') return;

    const productInfo = modal.querySelector('product-info');
    if (productInfo && typeof productInfo.updateMedia === 'function' && !productInfo.__klassyQvUpdateMediaPatched) {
      productInfo.__klassyQvUpdateMediaPatched = true;
      productInfo.updateMedia = function () {
        /* no-op: el color-filter del drawer gestiona la visibilidad de slides.
           Dawn sigue actualizando price/SKU/inventory normalmente. */
      };
    }

    //   3) Override resetProductFormState → no deshabilitar (gris) el botón
    //      "Add to cart" en cada cambio de color. Dawn lo deshabilita ANTES del
    //      fetch de sección y lo re-habilita ~300ms después → parpadeo gris en
    //      cada swatch. Mantenemos el limpiado de errores pero quitamos el
    //      pre-disable; el estado final (disponible/agotado) lo sigue fijando
    //      handleUpdateProductInfo tras el fetch. Mismo fix que en color-filter.js.
    if (productInfo && typeof productInfo.resetProductFormState === 'function' && !productInfo.__klassyQvResetPatched) {
      productInfo.__klassyQvResetPatched = true;
      productInfo.resetProductFormState = function () {
        this.productForm?.handleErrorMessage();
      };
    }

    requestAnimationFrame(() => {
      // 1) Bind onload listeners para fade-in suave en cada slide.
      bindImageLoadListeners(modal);
      // 2) Hover/focus en swatches → precarga anticipada.
      bindSwatchPrefetch(modal);
      // 3) Aplicar color filter al default (con preload del color actual).
      applyColorFilter(modal);
      // 3b) Fijar el id de variante inicial (por si el default no cuadra).
      syncVariantId(modal);
      // 4) Precarga progresiva en background del resto de colores
      //    (saltada si conexión slow-2g / save-data).
      preloadProgressive(modal);
    });
  });
})();
